import React from "react"
import Editor from "rich-markdown-editor"
import { Props as EditorProps } from "rich-markdown-editor"
import type { TreeItem } from "@atlaskit/tree/types"
import { ClassProps } from "../../utility/common/props"
import { Dispatch } from "../../utility/common/storeHelper"
import { useContentExtensions } from "../../hooks/useContentExtensions"
import { PageLayoutContentId } from "../PageLayout/PageLayout"
import ArticleViewLoader from "../ArticleView/ArticleViewLoader"
import { EMPTY_MESSAGE_INPUT } from "../../store/dialogs/constants"

export const DEFAULT_RENDERER_DIS_EXT: NonNullable<EditorProps["disableExtensions"]> = [
    "code_block",
    "code_fence",
    "container_notice",
    "emoji",
    "emojimenu",
    "widget",
    "quickactions"
]

export interface ContentRendererProps extends ClassProps {
    content: string
    linkify?: boolean
    disableExtensions?: EditorProps["disableExtensions"]
    projectId?: string
    onOpenArticle?: (articleCode: string, categoryItem?: TreeItem, catalogCode?: string) => void
    onChangeLocation?: (path: string) => void
    onDispatch?: Dispatch
    channels?: string[]
    articleSymbolCode?: string
    allowedFileExtensions?: string[]
    allowedVideoExtensions?: string[]
    allowedAudioExtensions?: string[]
    onInit?: () => void
    onDocStateChange?: (getContent: () => string) => void
}

const ContentRendererInner = (props: ContentRendererProps) => {
    const {
        content,
        linkify,
        className,
        disableExtensions = DEFAULT_RENDERER_DIS_EXT,
        projectId,
        onOpenArticle,
        onChangeLocation,
        onDispatch,
        channels,
        articleSymbolCode,
        allowedFileExtensions = [],
        allowedVideoExtensions = [],
        allowedAudioExtensions = [],
        onInit,
        onDocStateChange
    } = props

    const { extensions, embeds, dictionary, isLoadingSticker } = useContentExtensions({
        projectId,
        onOpenArticle,
        onChangeLocation,
        onDispatch,
        channels,
        articleSymbolCode,
        allowedFileExtensions,
        allowedVideoExtensions,
        allowedAudioExtensions
    })

    if (isLoadingSticker) {
        return <ArticleViewLoader />
    }

    const value = content && content.length > 0 ? content : EMPTY_MESSAGE_INPUT

    return (
        <Editor
            className={className}
            value={value}
            readOnly
            linkify={linkify}
            autoFocus={false}
            onChange={() => void 0}
            onDocStateChange={onDocStateChange}
            onInit={onInit}
            allowedFileExtensions={allowedFileExtensions}
            allowedVideoExtensions={allowedVideoExtensions}
            allowedAudioExtensions={allowedAudioExtensions}
            embeds={embeds}
            dictionary={dictionary}
            extensions={extensions}
            disableExtensions={disableExtensions}
            scrollableContainerId={PageLayoutContentId}
            hiddenTopOffset={120}
        />
    )
}

export const ContentRenderer = React.memo(ContentRendererInner)
